import React, { useState, useEffect } from "react";
import { Card, Typography, Table, Space, DatePicker, Input, Button, Tag, Row, Col, Tooltip, Avatar, Modal, Checkbox, Descriptions } from "antd";
import { SearchOutlined, UserOutlined, PrinterOutlined, CalendarOutlined, CloseOutlined, BankOutlined, InfoCircleOutlined, TeamOutlined, EyeOutlined } from "@ant-design/icons";
import DepartmentList from "../../component/list/DepartmentList";
import StaffList from "../../component/list/StaffList";
import { request } from "../../share/request";
import { getCurrentUser } from "../../share/getProfile";
import { linkPath } from "../../share/helper";
import GetLoading from "../../share/getLoading";
import dayjs from "dayjs";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const AttendanceOTPage = () => {
    const userProfile = getCurrentUser();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);

    const formatTimeAMPM = (timeStr) => {
        if (!timeStr || timeStr === 'null') return 'អវត្តមាន';
        try {
            const parts = timeStr.split(':');
            if (parts.length < 2) return timeStr;
            const hRaw = parseInt(parts[0], 10);
            const ampm = hRaw >= 12 ? 'PM' : 'AM';
            const h = hRaw % 12 || 12;
            return `${h.toString().padStart(2, '0')}:${parts[1]} ${ampm}`;
        } catch (e) {
            return timeStr;
        }
    };

    // Default to current week
    const [dateRange, setDateRange] = useState([dayjs().startOf("week"), dayjs().endOf("week")]);
    const [txtSearch, setTxtSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [downloadReportModal, setDownloadReportModal] = useState(false);
    const [selectedDateStart, setSelectedDateStart] = useState(dayjs().startOf("week"));
    const [selectedDateEnd, setSelectedDateEnd] = useState(dayjs().endOf("week"));
    const [downloadSelectedDepartments, setDownloadSelectedDepartments] = useState([]);
    const [downloadSelectedStaff, setDownloadSelectedStaff] = useState([]);
    const [shiftInfoModal, setShiftInfoModal] = useState(false);
    const [selectedShiftInfo, setSelectedShiftInfo] = useState(null);

    const handleDateChange_Start = (date) => setSelectedDateStart(date);
    const handleDateChange_End = (date) => setSelectedDateEnd(date);

    const handleOpenReport = async () => {
        setLoading(true);
        try {
            let staffId = "";
            if (downloadSelectedStaff.length > 0) {
              staffId = downloadSelectedStaff.join(",");
            }
        
            const params = new URLSearchParams({
                org_code: userProfile?.org_code || "",
                date_start: selectedDateStart ? dayjs(selectedDateStart).format("YYYY-MM-DD") : "",
                date_end: selectedDateEnd ? dayjs(selectedDateEnd).format("YYYY-MM-DD") : "",
                department_id: downloadSelectedDepartments.length > 0 ? downloadSelectedDepartments.join(",") : "",
                idnumber: staffId,
                txtSearch: txtSearch,
                page: 1,
                limit: 10000 // Get all data
            });

            const res = await request(`attendanceqrcode-byot?${params.toString()}`, "get");
            let reportData = [];
            if (res?.success && res?.api_attendance_time_report?.length > 0) {
                reportData = res.api_attendance_time_report[0] || [];
            }

            if (!reportData || reportData.length === 0) {
                Modal.warning({
                    title: 'មិនមានទិន្នន័យ',
                    content: 'មិនមានទិន្នន័យសម្រាប់ទាញយករបាយការណ៍ទេ!',
                });
                setLoading(false);
                return;
            }

            const reportCols = ["No", "ផ្នែក", "ព័ត៌មានបុគ្គលិក"];
            const reportKeys = ["index", "department", "fullname"];
            const weekendIndices = [];
            
            const fixedStartCols = ["id", "image", "department_name", "full_name", "position_kh"];
            const fixedEndCols = ["total_hours", "total_late_minutes", "total_early_minutes", "percentages"];
            
            let dateKeys = Object.keys(reportData[0]).filter(key => !fixedStartCols.includes(key) && !fixedEndCols.includes(key));
            
            const khmerDays = ["អាទិត្យ", "ច័ន្ទ", "អង្គារ", "ពុធ", "ព្រហ", "សុក្រ", "សៅរ៍"];
            
            dateKeys.forEach((dateStr) => {
                const dateObj = dayjs(dateStr);
                const dayIdx = dateObj.day();
                reportCols.push(`${khmerDays[dayIdx]} ${dateObj.format('DD/MM')}`);
                reportKeys.push(dateStr);
                if (dayIdx === 0 || dayIdx === 6) {
                    weekendIndices.push(reportCols.length - 1);
                }
            });
            
            reportCols.push("សរុបម៉ោង", "យឺត(នាទី)", "ចេញមុន(នាទី)", "ភាគរយ");
            reportKeys.push("total_hours", "total_late_minutes", "total_early_minutes", "percentages");

            const reportRows = reportData.map((row, index) => {
                const r = [];
                r.push(index + 1);
                r.push(row.department_name || "");
                
                const staffInfo = `<div style="line-height:1.2;">
                    <div style="font-weight:bold; font-size:14px; margin-bottom:4px;">${row.full_name || ""}</div>
                    <div style="color:gray; font-size:12px;">${row.position_kh || ""}</div>
                </div>`;
                r.push(staffInfo);
                
                dateKeys.forEach(dateStr => {
                    const cellText = row[dateStr];
                    if (!cellText || cellText === "null") {
                        r.push("-");
                    } else {
                        try {
                            const shifts = JSON.parse(cellText);
                            let cellContent = "";
                            shifts.forEach(shift => {
                                const inT = formatTimeAMPM(shift.scan_time?.in);
                                const outT = formatTimeAMPM(shift.scan_time?.out);
                                const late = shift.late_minutes > 0 ? `<span style="color:red;">${shift.late_minutes}m</span>` : "0m";
                                const early = shift.early_minutes > 0 ? `<span style="color:orange;">${shift.early_minutes}m</span>` : "0m";
                                const pNum = parseFloat(shift.percentages) || 0;
                                const displayPercent = pNum > 100 ? 100 : pNum;
                                const percent = displayPercent >= 100 ? `<span style="color:green;">${displayPercent}%</span>` : `<span style="color:red;">${displayPercent}%</span>`;
                                cellContent += `<div style="font-size:11px; line-height:1.4; border-bottom:1px dashed #e8e8e8; padding-bottom:4px; margin-bottom:4px;">
                                    <div style="color:#1890ff; font-weight:bold;">${shift.shift_name_kh}</div>
                                    <div style="display:flex; justify-content:space-between;">
                                        <span>ចូល: ${inT}</span>
                                        <span>ចេញ: ${outT}</span>
                                    </div>
                                    <div style="display:flex; justify-content:space-between;">
                                        <span>ម៉ោង: ${shift.total_hours}</span>
                                        <span>យឺត: ${late}</span>
                                    </div>
                                    <div style="display:flex; justify-content:space-between;">
                                        <span>ចេញមុន: ${early}</span>
                                        <span>ភាគរយ: ${percent}</span>
                                    </div>
                                </div>`;
                            });
                            r.push(cellContent);
                        } catch(e) {
                            r.push("-");
                        }
                    }
                });
                
                r.push(row.total_hours || 0);
                r.push(row.total_late_minutes || 0);
                r.push(row.total_early_minutes || 0);
                const pNum = parseFloat(row.percentages) || 0;
                const displayPercent = pNum > 100 ? 100 : pNum;
                r.push(`${displayPercent}%`);
                
                return r;
            });

            const payload = {
                orgName: userProfile?.org_name_kh || userProfile?.org_name || (userProfile?.org_code ? `អង្គភាពកូដ: ${userProfile.org_code}` : "របាយការណ៍"), 
                title: `របាយការណ៍ស្កែនវត្តមានបន្ថែមម៉ោង ពី ${selectedDateStart.format('DD/MM/YYYY')} ដល់ ${selectedDateEnd.format('DD/MM/YYYY')}`,
                columns: reportCols,
                keys: reportKeys,
                rows: reportRows,
                weekendIndices: weekendIndices
            };

            localStorage.setItem("attendance_report_data", JSON.stringify(payload));
            window.open("/reportattendance-byapplyshift.html", "_blank");
            setDownloadReportModal(false);
        } catch (error) {
            console.error("Error generating report:", error);
        } finally {
            setLoading(false);
        }
    };

    const [columns, setColumns] = useState([]);

    const fetchData = async (currentPage = page, currentLimit = pageSize) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                org_code: userProfile?.org_code || "",
                date_start: dateRange[0] ? dateRange[0].format("YYYY-MM-DD") : "",
                date_end: dateRange[1] ? dateRange[1].format("YYYY-MM-DD") : "",
                txtSearch: txtSearch,
                page: currentPage,
                limit: currentLimit
            });

            const res = await request(`attendanceqrcode-byot?${params.toString()}`, "get");

            if (res?.success && res?.api_attendance_time_report?.length > 0) {
                const rows = res.api_attendance_time_report[0] || [];
                const totalRecords = res.api_attendance_time_report[1]?.[0]?.total_record || 0;

                setData(rows);
                setTotal(totalRecords);
                buildColumns(rows);
            } else {
                setData([]);
                setTotal(0);
                setColumns([]);
            }
        } catch (error) {
            console.error("Error fetching OT attendance:", error);
        } finally {
            setLoading(false);
        }
    };

    const buildColumns = (rows) => {
        if (!rows || rows.length === 0) return;

        // Standard fixed columns
        const fixedStartCols = ["id", "image", "department_name", "full_name", "position_kh"];
        const fixedEndCols = ["total_hours", "total_late_minutes", "total_early_minutes", "percentages"];

        // Extract dynamic date columns from the first row
        const firstRowKeys = Object.keys(rows[0]);
        const dateKeys = firstRowKeys.filter(key => !fixedStartCols.includes(key) && !fixedEndCols.includes(key));

        const cols = [];

        // 1. Add Start Columns
        cols.push({
            title: "No",
            key: "index",
            fixed: 'left',
            width: 60,
            align: 'center',
            render: (text, record, index) => ((page - 1) * pageSize) + index + 1
        });
        cols.push({
            title: "ឈ្មោះបុគ្គលិក",
            key: "employee_info",
            fixed: 'left',
            width: 250,
            render: (_, record) => (
                <Space size="middle" align="start">
                    <Avatar
                        size={50}
                        src={record.image ? `${linkPath}${record.image}` : null}
                        icon={<UserOutlined />}
                    />
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>{record.full_name}</span>
                        <span style={{ color: 'gray', fontSize: '12px' }}>{record.department_name}</span>
                        <span style={{ color: 'gray', fontSize: '12px' }}>{record.position_kh}</span>
                    </div>
                </Space>
            )
        });

        // 2. Add Dynamic Date Columns
        const khmerDays = ["អាទិត្យ", "ច័ន្ទ", "អង្គារ", "ពុធ", "ព្រហ", "សុក្រ", "សៅរ៍"];
        const englishDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        dateKeys.forEach(dateStr => {
            const dateObj = dayjs(dateStr);
            const dayIdx = dateObj.day();
            const headerText = `${khmerDays[dayIdx]}-${englishDays[dayIdx]}`;
            const formattedDate = dateObj.format('DD/MM');

            cols.push({
                title: (
                    <div style={{ textAlign: 'center' }}>
                        <span>{headerText}</span><br />
                        <span style={{ fontSize: '12px', color: 'gray' }}>{formattedDate}</span>
                    </div>
                ),
                dataIndex: dateStr,
                key: dateStr,
                width: 180,
                render: (text) => {
                    if (!text) return <span style={{ color: '#ccc' }}>-</span>;
                    try {
                        const shifts = JSON.parse(text);
                        return (
                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                {shifts.map((shiftObj, idx) => {
                                    const shift = shiftObj;
                                    const inTime = shift.scan_time?.in;
                                    const outTime = shift.scan_time?.out;
                                    const pNum = parseFloat(shift.percentages) || 0;
                                    const displayPercent = pNum > 100 ? 100 : pNum;

                                    return (
                                        <div key={idx} style={{ padding: 8, border: '1px solid #f0f0f0', borderRadius: 6, marginBottom: 4, backgroundColor: '#fafafa', width: '100%' }}>
                                            <div style={{ fontWeight: 'bold', color: '#1890ff', marginBottom: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <span>{shift.shift_name_kh}</span>
                                                <EyeOutlined 
                                                    style={{ cursor: 'pointer', color: '#52c41a', fontSize: '15px' }}
                                                    onClick={() => {
                                                        setSelectedShiftInfo(shift);
                                                        setShiftInfoModal(true);
                                                    }}
                                                />
                                            </div>
                                            <div style={{ fontSize: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div><b>ចូល:</b> <Tag color={inTime && inTime !== 'null' ? 'blue' : 'default'} style={{ margin: 0, float: 'right' }}>{formatTimeAMPM(inTime)}</Tag></div>
                                                <div><b>ចេញ:</b> <Tag color={outTime && outTime !== 'null' ? 'blue' : 'default'} style={{ margin: 0, float: 'right' }}>{formatTimeAMPM(outTime)}</Tag></div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span><b>សរុបម៉ោង:</b> {shift.total_hours}</span>
                                                    <span><b>យឺត:</b> <span style={{ color: shift.late_minutes > 0 ? 'red' : 'inherit' }}>{shift.late_minutes}m</span></span>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span><b>ចេញមុន:</b> <span style={{ color: shift.early_minutes > 0 ? 'orange' : 'inherit' }}>{shift.early_minutes}m</span></span>
                                                    <span><b>ភាគរយ:</b> <Tag color={displayPercent >= 100 ? 'green' : 'red'} style={{ margin: 0 }}>{displayPercent}%</Tag></span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </Space>
                        );
                    } catch (e) {
                        return <span>Error Parsing</span>;
                    }
                }
            });
        });

        // 3. Add End Columns
        cols.push({
            title: "សរុបម៉ោង", dataIndex: "total_hours", key: "total_hours", fixed: 'right', width: 100, align: 'center',
            className: 'col-total-hours',
            render: (val) => <b>{val}</b>
        });
        cols.push({
            title: "យឺត", dataIndex: "total_late_minutes", key: "total_late_minutes", fixed: 'right', width: 100, align: 'center',
            className: 'col-late',
            render: (val) => <span style={{ color: val > 0 ? 'red' : 'inherit' }}>{val}</span>
        });
        cols.push({
            title: "ចេញមុន", dataIndex: "total_early_minutes", key: "total_early_minutes", fixed: 'right', width: 100, align: 'center',
            className: 'col-early',
            render: (val) => <span style={{ color: val > 0 ? 'orange' : 'inherit' }}>{val}</span>
        });
        cols.push({
            title: "ភាគរយ", dataIndex: "percentages", key: "percentages", fixed: 'right', width: 100, align: 'center',
            className: 'col-percentage',
            render: (val) => {
                const num = parseFloat(val) || 0;
                const display = num > 100 ? 100 : num;
                return <Tag color={display >= 100 ? 'green' : 'orange'}>{display}%</Tag>;
            }
        });

        setColumns(cols);
    };

    useEffect(() => {
        // Fetch data automatically when tab mounts
        fetchData(1, pageSize);
    }, []);


    const handleTableChange = (pagination) => {
        setPage(pagination.current);
        setPageSize(pagination.pageSize);
        fetchData(pagination.current, pagination.pageSize);
    };

    return (
        <Card bordered={false}>
            <style>
                {`
                    .full-height-table .ant-table-body {
                        min-height: calc(95vh - 330px);
                    }
                    .full-height-table,
                    .full-height-table .ant-table-thead > tr > th,
                    .full-height-table .ant-table-tbody > tr > td,
                    .full-height-table .ant-tag {
                        font-family: 'Khmer OS Battambang', sans-serif !important;
                    }
                    .full-height-table .ant-table-thead > tr > th {
                        padding: 8px 8px !important;
                    }
                    .col-total-hours { background-color: #f6ffed !important; }
                    th.col-total-hours { background-color: #d9f7be !important; }
                    .col-late { background-color: #fff1f0 !important; }
                    th.col-late { background-color: #ffccc7 !important; }
                    .col-early { background-color: #fffbe6 !important; }
                    th.col-early { background-color: #ffe58f !important; }
                    .col-percentage { background-color: #e6f4ff !important; }
                    th.col-percentage { background-color: #bae0ff !important; }
                `}
            </style>

            <Modal
                title={
                    <Space>
                        <CalendarOutlined />
                        <span>ទាញរបាយការណ៍បន្ថែមម៉ោង</span>
                    </Space>
                }
                className="custom-modal"
                open={downloadReportModal}
                width={700}
                onCancel={() => setDownloadReportModal(false)}
                footer={[
                    <div key="footer">
                        <Space wrap>
                            <Button
                                danger
                                variant="solid"
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    setDownloadReportModal(false);
                                    setDownloadSelectedDepartments([]);
                                    setDownloadSelectedStaff([]);
                                }}
                            >
                                បោះបង់
                            </Button>
                            <Button
                                type="primary"
                                icon={<PrinterOutlined />}
                                onClick={handleOpenReport}
                                loading={loading}
                            >
                                បើករបាយការណ៍
                            </Button>
                        </Space>
                    </div>
                ]}
            >
                <div style={{ padding: "10px 0" }}>
                    {/* Section 1: Date Range (Required) */}
                    <div style={{
                        marginBottom: 24,
                        padding: "16px",
                        backgroundColor: "#fafafa",
                        borderRadius: "8px",
                        border: "1px solid #e8e8e8"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "12px",
                            gap: "8px"
                        }}>
                            <div style={{
                                backgroundColor: "#1890ff",
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                fontWeight: "bold",
                                flexShrink: 0
                            }}>
                                1
                            </div>
                            <CalendarOutlined style={{ color: "#1890ff", fontSize: "18px" }} />
                            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
                                ជ្រើសរើសកាលបរិច្ឆេទ <span style={{ color: "red" }}>*</span>
                            </span>
                        </div>

                        <div style={{ display: "flex", flexDirection: "row", gap: "20px", flexWrap: "wrap", marginLeft: "32px" }}>
                            <div>
                                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                    ចាប់ពីថ្ងៃទីខែ :
                                </label>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    onChange={handleDateChange_Start}
                                    value={selectedDateStart}
                                    style={{ width: "200px" }}
                                    placeholder="ជ្រើសរើសថ្ងៃចាប់ផ្តើម"
                                />
                            </div>
                            <div>
                                <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>
                                    ដល់ថ្ងៃទីខែ :
                                </label>
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    onChange={handleDateChange_End}
                                    value={selectedDateEnd}
                                    style={{ width: "200px" }}
                                    placeholder="ជ្រើសរើសថ្ងៃបញ្ចប់"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Department Selection (Optional) */}
                    <div style={{
                        marginBottom: 24,
                        padding: "16px",
                        backgroundColor: "#fafafa",
                        borderRadius: "8px",
                        border: "1px solid #e8e8e8"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "12px",
                            gap: "8px"
                        }}>
                            <div style={{
                                backgroundColor: "#52c41a",
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                fontWeight: "bold",
                                flexShrink: 0
                            }}>
                                2
                            </div>
                            <BankOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
                            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
                                ជ្រើសរើសនាយកដ្ឋាន
                            </span>
                            <Tag color="default" style={{ marginLeft: "4px" }}>ជម្រើស</Tag>
                        </div>

                        <div style={{ marginLeft: "32px" }}>
                            <DepartmentList
                                selectedDepartments={downloadSelectedDepartments}
                                onChange={setDownloadSelectedDepartments}
                                placeholder="ស្វែងរក និងជ្រើសរើសនាយកដ្ឋាន..."
                                showCode={true}
                                multiple={true}
                                allowClear={true}
                                maxTagCount={5}
                                showSelectAll={true}
                            />

                            {downloadSelectedDepartments.length > 0 && (
                                <div style={{
                                    marginTop: 8,
                                    fontSize: "12px",
                                    color: "#52c41a",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px"
                                }}>
                                    <InfoCircleOutlined />
                                    <span>បុគ្គលិកនឹងត្រូវបានតម្រងតាមនាយកដ្ឋានដែលបានជ្រើសរើស</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Section 3: Staff Selection (Optional) */}
                    <div style={{
                        marginBottom: 16,
                        padding: "16px",
                        backgroundColor: "#fafafa",
                        borderRadius: "8px",
                        border: "1px solid #e8e8e8"
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "12px",
                            gap: "8px",
                            flexWrap: "wrap"
                        }}>
                            <div style={{
                                backgroundColor: "#722ed1",
                                color: "white",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "14px",
                                fontWeight: "bold",
                                flexShrink: 0
                            }}>
                                3
                            </div>
                            <TeamOutlined style={{ color: "#722ed1", fontSize: "18px" }} />
                            <span style={{ fontWeight: "bold", fontSize: "16px" }}>
                                ជ្រើសរើសបុគ្គលិក
                            </span>
                            <Tag color="default">ជម្រើស</Tag>
                        </div>

                        <div style={{ marginLeft: "32px" }}>
                            <StaffList
                                selectedStaff={downloadSelectedStaff}
                                onChange={setDownloadSelectedStaff}
                                placeholder="ស្វែងរក និងជ្រើសរើសបុគ្គលិក..."
                                multiple={true}
                                allowClear={true}
                                maxTagCount={5}
                                showAvatar={true}
                                showSelectAll={true}
                                departmentCode={downloadSelectedDepartments.length > 0 ? downloadSelectedDepartments.join(",") : null}
                            />
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                title={<span style={{ fontFamily: "'Khmer OS Battambang', sans-serif", fontWeight: 'bold' }}>ព័ត៌មានលម្អិតនៃវេន</span>}
                open={shiftInfoModal}
                onCancel={() => setShiftInfoModal(false)}
                footer={null}
                width={600}
            >
                {selectedShiftInfo && selectedShiftInfo.shift_info && (
                    <Descriptions bordered column={1} size="small" style={{ marginTop: 16 }} labelStyle={{ fontFamily: "'Khmer OS Battambang', sans-serif" }} contentStyle={{ fontFamily: "'Khmer OS Battambang', sans-serif" }}>
                        <Descriptions.Item label="ឈ្មោះវេន (ខ្មែរ)">{selectedShiftInfo.shift_info.shift_name_kh}</Descriptions.Item>
                        <Descriptions.Item label="ឈ្មោះវេន (English)">{selectedShiftInfo.shift_info.shift_name}</Descriptions.Item>
                        <Descriptions.Item label="ម៉ោងចូល">{formatTimeAMPM(selectedShiftInfo.shift_info.on_duty_time)}</Descriptions.Item>
                        <Descriptions.Item label="ម៉ោងចេញ">{formatTimeAMPM(selectedShiftInfo.shift_info.off_duty_time)}</Descriptions.Item>
                        <Descriptions.Item label="អនុញ្ញាតយឺត (នាទី)">{selectedShiftInfo.shift_info.late_minutes}</Descriptions.Item>
                        <Descriptions.Item label="អនុញ្ញាតចេញមុន (នាទី)">{selectedShiftInfo.shift_info.leave_early_minutes}</Descriptions.Item>
                        <Descriptions.Item label="ចាប់ផ្តើមម៉ោងចូល (ម៉ោង)">{formatTimeAMPM(selectedShiftInfo.shift_info.beginning_in_start)}</Descriptions.Item>
                        <Descriptions.Item label="បញ្ចប់ម៉ោងចូល (ម៉ោង)">{formatTimeAMPM(selectedShiftInfo.shift_info.beginning_in_end)}</Descriptions.Item>
                        <Descriptions.Item label="ចាប់ផ្តើមម៉ោងចេញ (ម៉ោង)">{formatTimeAMPM(selectedShiftInfo.shift_info.ending_in_start)}</Descriptions.Item>
                        <Descriptions.Item label="បញ្ចប់ម៉ោងចេញ (ម៉ោង)">{formatTimeAMPM(selectedShiftInfo.shift_info.ending_in_end)}</Descriptions.Item>
                        <Descriptions.Item label="ឆ្លងចូលថ្ងៃបន្ទាប់ (ឆ្លងអធ្រាត្រ)">
                            <Tag color={selectedShiftInfo.shift_info.next_day === 1 ? 'red' : 'green'}>
                                {selectedShiftInfo.shift_info.next_day === 1 ? 'ឆ្លងថ្ងៃបន្ទាប់' : 'ថ្ងៃតែមួយ'}
                            </Tag>
                        </Descriptions.Item>
                    </Descriptions>
                )}
            </Modal>

            {loading && (
                <div className="sprinCustome">
                    <GetLoading />
                </div>
            )}
            <Space direction="vertical" size="large" style={{ width: "100%" }}>

                <Row gutter={[16, 16]} align="middle">
                    <Col>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates)}
                            allowClear={false}
                        />
                    </Col>
                    <Col>
                        <Input
                            placeholder="ស្វែងរក ឈ្មោះបុគ្គលិក ឬ ផ្នែក..."
                            value={txtSearch}
                            onChange={(e) => setTxtSearch(e.target.value)}
                            allowClear
                            style={{ width: 300 }}
                        />
                    </Col>
                    <Col>
                        <Button type="primary" icon={<SearchOutlined />} onClick={() => { setPage(1); fetchData(1, pageSize); }} loading={loading}>
                            ស្វែងរក
                        </Button>
                    </Col>
                    <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button 
                            className="buttonstylekhmer"
                            type="primary" 
                            icon={<PrinterOutlined />} 
                            onClick={() => {
                                setSelectedDateStart(dateRange[0]);
                                setSelectedDateEnd(dateRange[1]);
                                setDownloadReportModal(true);
                            }}
                        >
                            ទាញរបាយការណ៍
                        </Button>
                    </Col>
                </Row>

                <Table
                    className="full-height-table"
                    dataSource={data}
                    columns={columns}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content', y: 'calc(100vh - 330px)' }}
                    pagination={{
                        current: page,
                        pageSize: pageSize,
                        total: total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100']
                    }}
                    onChange={handleTableChange}
                    bordered
                />
            </Space>
        </Card>
    );
};

export default AttendanceOTPage;
